import React, { useState, useRef } from "react";
import { Camera, Loader2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * QuickPhotoButton — one-tap "share a shot" flow for the homepage reel
 * strip. Skips the full trip-story form and just posts a photo + optional
 * caption to /api/wall/photo.
 *
 * Anonymous users get a soft prompt to log in. Signed-in users see a
 * modal with a file picker + short caption input.
 */
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.82;

async function _fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_SIDE || height > MAX_SIDE) {
          const scale = Math.min(MAX_SIDE / width, MAX_SIDE / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function QuickPhotoButton({ className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image too large — pick something under 15 MB");
      return;
    }
    try {
      const url = await _fileToCompressedDataUrl(file);
      setDataUrl(url);
    } catch {
      toast.error("Could not read that image — try another one");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!dataUrl) {
      toast.error("Pick a photo first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/wall/photo", {
        image_data_url: dataUrl,
        caption: caption.trim() || null,
      });
      toast.success("Shared! Thanks for adding to the wall 🏕");
      setOpen(false);
      setDataUrl("");
      setCaption("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <button
        onClick={() => toast("Log in to post a photo to the wall.", { icon: "🔐" })}
        className={className}
        data-testid="quick-photo-cta-anon"
      >
        <Camera className="w-4 h-4" /> Quick photo
      </button>
    );
  }

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={className}
        data-testid="quick-photo-open"
      >
        <Camera className="w-4 h-4" /> Quick photo
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="quick-photo-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white max-w-md w-full p-6 border-2 border-[#ff4500] shadow-[0_8px_0_0_#111]"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="eyebrow text-[#ff4500]">Quick share</div>
                <h4 className="font-display text-2xl mt-1">Post a photo</h4>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 hover:bg-[#fafafa]"
                aria-label="Close"
                data-testid="quick-photo-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-[#555] leading-relaxed mb-4">
              One tap · no trip form. Just drop a photo (and a line if you want) and it&apos;ll appear on the community wall.
            </p>

            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="quick-photo-file" />

            {dataUrl ? (
              <div className="relative mb-4">
                <img src={dataUrl} alt="preview" className="w-full max-h-72 object-contain bg-black/5 border hairline" />
                <button
                  type="button"
                  onClick={() => setDataUrl("")}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#111] px-2 py-1 text-[10px] mono uppercase tracking-wider border hairline"
                >
                  Replace
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={pickFile}
                className="w-full mb-4 py-8 border-2 border-dashed border-[#e5e5e5] hover:border-[#ff4500] text-[#666] hover:text-[#ff4500] flex flex-col items-center gap-2 transition"
                data-testid="quick-photo-picker"
              >
                <ImagePlus className="w-7 h-7" />
                <span className="text-sm">Choose a photo</span>
                <span className="text-[10px] mono uppercase tracking-wider text-[#888]">Max 15 MB · JPG / PNG / HEIC</span>
              </button>
            )}

            <label className="block mb-4">
              <span className="eyebrow block mb-1.5">Caption (optional)</span>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 200))}
                placeholder="e.g. Golden hour at the Peaks — worth every mile"
                maxLength={200}
                className="w-full border hairline px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff4500]"
                data-testid="quick-photo-caption"
              />
              <span className="text-[10px] mono text-[#888] block mt-1 text-right">{caption.length} / 200</span>
            </label>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs mono uppercase tracking-wider px-4 py-2 border hairline hover:bg-[#fafafa]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !dataUrl}
                className="btn-primary text-xs px-5 py-2 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="quick-photo-submit"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Share photo
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
